/**
 * Composes selection, viewport, and pan sub-hooks into a single regional map API.
 * Orchestrates reducer-backed selection, projection math, and zoom gating so the
 * UI wrapper can stay declarative and stateless.
 * Author: bb
 */

import { useEffect } from "react";
import { useSelectionModel } from "./useSelectionModel";
import { useViewport } from "./useViewport";
import { usePanMode } from "./usePanMode";
import type { UseRegionalFilterStateResult } from "./RegionalFilter.types";

const isDevEnvironment =
  typeof import.meta !== "undefined" ? import.meta.env?.DEV ?? false : false;

const regionalMapLog = (...args: any[]) => {
  if (!isDevEnvironment) return;
  console.log("[RegionalFilter]", ...args);
};

/**
 * Composes the regional map sub-hooks into a single consumable state bundle.
 * - useSelectionModel: reducer for activeState/detailLayer/selection set, plus derived lookups.
 * - useViewport: geography filtering + projection center/scale + sizing.
 * - usePanMode: shift/button-based pan mode and zoom filtering.
 */
export function useRegionalFilterState(): UseRegionalFilterStateResult {
  const selectionModel = useSelectionModel();
  const viewport = useViewport(selectionModel.detailLayer, selectionModel.activeState);
  const pan = usePanMode(selectionModel.activeState);

  useEffect(() => {
    regionalMapLog("selection updated", {
      activeState: selectionModel.activeState,
      detailLayer: selectionModel.detailLayer,
      selectedRegions: Array.from(selectionModel.selection.regions),
    });
  }, [selectionModel.activeState, selectionModel.detailLayer, selectionModel.selection.regions]);

  return {
    selection: selectionModel.selection,
    activeState: selectionModel.activeState,
    detailLayer: selectionModel.detailLayer,
    availableDetailLayers: selectionModel.availableDetailLayers,
    hasAllSelected: selectionModel.hasAllSelected,
    selectedStateSet: selectionModel.selectedStateSet,
    regionSelectionsByState: selectionModel.regionSelectionsByState,
    geography: viewport.geography,
    allLayers: viewport.allLayers,
    projectionCenter: viewport.projectionCenter,
    projectionScale: viewport.projectionScale,
    zoomGroupKey: viewport.zoomGroupKey,
    width: viewport.width,
    height: viewport.height,
    panModeEnabled: pan.panModeEnabled,
    panModeActive: pan.panModeActive,
    setPanModeEnabled: pan.setPanModeEnabled,
    handleZoomFilter: pan.handleZoomFilter,
    handleSelection: selectionModel.handleSelection,
    setDetailLayer: selectionModel.setDetailLayer,
    resetSelection: selectionModel.resetSelection,
  };
}
